import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, Menu, X, Phone } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_happy-homes-ahead/artifacts/0buyd3la_CW%20Logo%20%281%29.svg";
const PRE_APPROVAL_LINK = "https://cityworth.loanzify.io/register";
const TEAM_PHONE_TEL = "tel:+15715208688";

const NAV = [
  {
    label: "Buy",
    children: [
      { label: "Buy a Home", href: "/buy-a-home", desc: "Your guided path to homeownership" },
      { label: "First-Time Homebuyer Guide", href: "/first-time-homebuyer-guide", desc: "Every step explained, simply" },
      { label: "Mortgage Calculator", href: "/mortgage-calculator", desc: "Estimate your monthly payment" },
    ],
  },
  {
    label: "Loan Options",
    children: [
      { label: "Conventional", href: "/loans/conventional", desc: "Flexible, low down payment" },
      { label: "FHA", href: "/loans/fha", desc: "First-time buyer friendly" },
      { label: "VA", href: "/loans/va", desc: "$0 down for those who served" },
      { label: "USDA", href: "/loans/usda", desc: "$0 down in rural areas" },
    ],
  },
  {
    label: "Real Estate",
    href: "/real-estate",
  },
  {
    label: "Refinance",
    href: "/refinance",
  },
  {
    label: "Blog",
    href: "/blog",
  },
];

const DesktopDropdown = ({ item }) => (
  <div className="relative group">
    <button
      type="button"
      className="inline-flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-[#D41F30] transition-colors py-2"
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {item.label}
      <ChevronDown className="w-4 h-4 mt-0.5 transition-transform group-hover:rotate-180" />
    </button>
    <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-3 min-w-[280px]">
        {item.children.map((c) => (
          <a
            key={c.href}
            href={c.href}
            className="block rounded-xl px-4 py-3 hover:bg-red-50 transition-colors"
            data-testid={`nav-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="font-semibold text-gray-900 text-sm">{c.label}</div>
            {c.desc && <div className="text-xs text-gray-500 mt-0.5">{c.desc}</div>}
          </a>
        ))}
      </div>
    </div>
  </div>
);

export default function MainHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();
  const isHomebuyer = location.pathname === "/homebuyer";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-6">
        <a href="/" data-testid="logo-link" className="flex-shrink-0">
          <img
            src={LOGO_URL}
            alt="CityWorth"
            className="h-10 md:h-11 w-auto"
            data-testid="logo-image"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) =>
            item.children ? (
              <DesktopDropdown key={item.label} item={item} />
            ) : (
              <a
                key={item.label}
                href={item.href}
                className="text-sm font-semibold text-gray-700 hover:text-[#D41F30] transition-colors py-2"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          {isHomebuyer ? (
            <a
              href={TEAM_PHONE_TEL}
              className="hidden sm:inline-flex items-center bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-5 py-2 text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="header-cta-btn"
            >
              <Phone className="mr-2 w-4 h-4" />
              Call your team
            </a>
          ) : (
            <Button
              className="hidden sm:inline-flex bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-5 py-2 text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => window.open(PRE_APPROVAL_LINK, "_blank")}
              data-testid="header-cta-btn"
            >
              Get Pre-Approved
            </Button>
          )}

          <button
            type="button"
            className="lg:hidden p-2 -mr-2 text-gray-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    type="button"
                    onClick={() => setOpenGroup(openGroup === item.label ? null : item.label)}
                    className="w-full flex items-center justify-between py-3 text-base font-semibold text-gray-900"
                  >
                    {item.label}
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${
                        openGroup === item.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openGroup === item.label && (
                    <div className="pl-4 pb-2 space-y-1">
                      {item.children.map((c) => (
                        <a
                          key={c.href}
                          href={c.href}
                          className="block py-2 text-sm text-gray-700 hover:text-[#D41F30]"
                        >
                          {c.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  className="block py-3 text-base font-semibold text-gray-900"
                >
                  {item.label}
                </a>
              )
            )}
            {isHomebuyer ? (
              <a
                href={TEAM_PHONE_TEL}
                className="flex items-center justify-center gap-2 mt-4 bg-[#D41F30] text-white rounded-full px-5 py-3 text-sm font-semibold"
              >
                <Phone className="w-4 h-4" />
                Call your team
              </a>
            ) : (
              <a
                href={PRE_APPROVAL_LINK}
                target="_blank"
                rel="noreferrer"
                className="block mt-4 bg-[#D41F30] text-white text-center rounded-full px-5 py-3 text-sm font-semibold"
              >
                Get Pre-Approved
              </a>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
